import { defineStore } from 'pinia';
import { type CartItem } from '@/entities/cart/model/types/cart';
import { type Product } from '@/entities/products/model/types/product';
import { formatSum } from '@/shared/utils/formatSum';

export const useCartStore = defineStore('cart', () => {
  const { $cartService } = useNuxtApp();

  const items = ref<CartItem[]>([]);
  const totalAmount = ref<number>(0);

  const totalQuantity = computed(() => {
    return items.value.reduce((total, item) => total + item.quantity, 0);
  });

  const totalPriceNumber = computed(() => {
    return items.value.reduce((total, item) => {
      const itemPrice = item.product.price_with_discount || item.product.price;
      return total + itemPrice * item.quantity;
    }, 0);
  });

  const totalPrice = computed(() => formatSum(totalPriceNumber.value));

  const productsCount = computed(() => items.value.length);

  const getProductFromCartById = (productId: string) => {
    return items.value.find(item => item.product.id === productId);
  };

  const getCart = async (): Promise<void> => {
    try {
      const { data } = await $cartService.getCart();

      if (data) {
        items.value = data.items;
        totalAmount.value = data.total_amount;
      }
    } catch (error) {
      console.error('Ошибка при получении корзины:', error);
    }
  };

  const addToCart = async (product: Product): Promise<void> => {
    try {
      const newItem: CartItem = {
        product,
        quantity: 1,
        subtotal: product.price,
      };
      items.value.push(newItem);
      await $cartService.addToCart(product.id);
    } catch (error) {
      console.error('Ошибка при добавлении в корзину:', error);
    }
  };

  const removeFromCart = async (productId: string): Promise<void> => {
    try {
      items.value = items.value.filter(item => item.product.id !== productId);
      await $cartService.removeFromCart(productId);
    } catch (error) {
      console.error('Ошибка при удалении из корзины:', error);
    }
  };

  const updateItemQuantity = async (
    productId: string,
    operation: 'increment' | 'decrement',
  ): Promise<void> => {
    try {
      const existingItem = items.value.find(item => item.product.id === productId);
      if (existingItem) {
        existingItem.quantity += operation === 'increment' ? 1 : -1;
        existingItem.subtotal = existingItem.product.price * existingItem.quantity;
        await (operation === 'increment'
          ? $cartService.addToCart(productId)
          : $cartService.decrementProductAmount(productId));
      }
    } catch (error) {
      console.error(
        `Ошибка при ${operation === 'increment' ? 'увеличении' : 'уменьшении'} количества:`,
        error,
      );
    }
  };

  const incrementAmount = async (productId: string): Promise<void> => {
    await updateItemQuantity(productId, 'increment');
  };

  const decrementAmount = async (productId: string): Promise<void> => {
    await updateItemQuantity(productId, 'decrement');
  };

  const clearCart = async (): Promise<void> => {
    try {
      items.value = [];
      await $cartService.clearCart();
    } catch (error) {
      console.error('Ошибка при очистке корзины:', error);
    }
  };

  return {
    items,
    totalAmount,
    totalQuantity,
    totalPrice,
    totalPriceNumber,
    productsCount,
    getCart,
    addToCart,
    removeFromCart,
    incrementAmount,
    decrementAmount,
    clearCart,
    getProductFromCartById,
  };
});
